import { resolveProj4Projection, normalizeInputProjection, validateInputProjection } from "@/projection";
import type { UploadFetch, UploadStatus } from "@/upload";
import { getEnvVar, WGS84_EPSG } from "@/utils";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import proj4 from "proj4";
import { useEffect, useMemo, useRef } from "react";

export type FileBounds = {
    proj: string;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    color: string;
};

export type Bounds2D = Omit<FileBounds, "proj" | "color">;

export function useMapPreviewBounds(
    items: readonly { proj: string; bounds: Bounds2D | null; state: UploadStatus["kind"] }[],
): FileBounds[] {
    const key = JSON.stringify(items);

    return useMemo(() => {
        return items.flatMap(({ proj, bounds, state }) => {
            const projection = normalizeInputProjection(proj);
            const color = { queued: "#808080", uploading: "#CF00C6", completed: "#198038", failed: "#808080" }[state];
            return bounds && validateInputProjection(projection) ? [{ ...bounds, proj: projection, color }] : [];
        });
    }, [key]);
}

const MAPTILER_KEY = getEnvVar("VITE_MAPTILER_API_KEY");

function boundsToGeoJSON(bounds: FileBounds, proj4Name: string): GeoJSON.Feature<GeoJSON.Polygon> | null {
    try {
        const corners = [
            [bounds.minX, bounds.minY],
            [bounds.maxX, bounds.minY],
            [bounds.maxX, bounds.maxY],
            [bounds.minX, bounds.maxY],
            [bounds.minX, bounds.minY],
        ];

        const wgs84 = corners.map(([x, y]) => {
            const [lng, lat] = proj4(proj4Name, WGS84_EPSG, [x, y]);

            if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
                throw new Error("Invalid coordinate");
            }

            return [lng, lat] as [number, number];
        });

        return {
            type: "Feature",
            properties: { color: bounds.color },
            geometry: {
                type: "Polygon",
                coordinates: [wgs84],
            },
        };
    } catch {
        return null;
    }
}

const updateFeatures = async (map: maplibregl.Map, bounds: FileBounds[], fitBounds: boolean, request?: UploadFetch) => {
    const features: GeoJSON.Feature<GeoJSON.Polygon>[] = [];
    for (const b of bounds) {
        const extentX = Math.abs(b.maxX - b.minX);
        const extentY = Math.abs(b.maxY - b.minY);
        if (extentX === 0 && extentY === 0) continue;

        let projection: string;
        try {
            projection = await resolveProj4Projection(b.proj, request);
        } catch {
            continue;
        }

        const feature = boundsToGeoJSON(b, projection);
        if (feature) features.push(feature);
    }

    const source = map.getSource("file-bounds") as maplibregl.GeoJSONSource;

    source.setData({
        type: "FeatureCollection",
        features,
    });

    if (fitBounds && features.length > 0) {
        const lngLatBounds = new maplibregl.LngLatBounds();
        for (const f of features) {
            for (const ring of f.geometry.coordinates) {
                for (const [lng, lat] of ring) {
                    lngLatBounds.extend([lng, lat]);
                }
            }
        }

        const canvas = map.getCanvas();
        const isHidden = canvas.classList.contains("hidden");
        const duration = isHidden ? 0 : 500;
        map.fitBounds(lngLatBounds, { padding: 40, maxZoom: 15, duration });
    }

    const canvas = map.getCanvas();
    if (canvas) canvas.classList.remove("hidden");
};

type MapPreviewOptions = Pick<maplibregl.MapOptions, "center" | "zoom" | "interactive">;

function initializeMap(
    container: HTMLDivElement,
    { center = [0, 20], zoom = 1, interactive = true }: MapPreviewOptions,
) {
    const map = new maplibregl.Map({
        container: container,
        style: `https://api.maptiler.com/maps/dataviz/style.json?key=${MAPTILER_KEY}`,
        center,
        zoom,
        interactive,
        attributionControl: false,
    });

    map.once("load", () => {
        map.addSource("file-bounds", {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
        });

        map.addLayer({
            id: "file-bounds-fill",
            type: "fill",
            source: "file-bounds",
            paint: {
                "fill-color": ["get", "color"],
                "fill-opacity": 0.1,
            },
        });

        map.addLayer({
            id: "file-bounds-line",
            type: "line",
            source: "file-bounds",
            paint: {
                "line-color": ["get", "color"],
                "line-width": 1,
            },
        });
    });

    return map;
}

export const MapPreview: React.FC<
    { bounds: FileBounds[]; className?: string; request?: UploadFetch } & MapPreviewOptions
> = ({ bounds, request, className, center, zoom, interactive }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<maplibregl.Map | null>(null);
    const geometryKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!containerRef.current || !MAPTILER_KEY) return;
        mapRef.current = initializeMap(containerRef.current, { center, zoom, interactive });
        geometryKeyRef.current = null;

        const canvas = mapRef.current?.getCanvas();

        if (canvas) {
            canvas.classList.add("hidden");
        }

        return () => {
            mapRef.current?.remove();
            mapRef.current = null;
        };
    }, [center, zoom, interactive]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const update = () => {
            const geometryKey = JSON.stringify(bounds.map(({ color: _color, ...geometry }) => geometry));
            updateFeatures(map, bounds, geometryKey !== geometryKeyRef.current, request);
            geometryKeyRef.current = geometryKey;
        };

        if (map.getSource("file-bounds")) {
            update();
        } else {
            map.once("load", update);
            return () => {
                map.off("load", update);
            };
        }
    }, [bounds, center, zoom, interactive, request]);

    return (
        <div className={`relative ${className}`}>
            <div ref={containerRef} className={`h-full w-full rounded-md bg-gray-15`} />
            <span
                style={{ WebkitTextStroke: "0.5px rgba(255,255,255,0)" }}
                className="absolute bottom-0.5 right-1 text-[0.5rem] leading-none text-black"
            >
                © MapTiler © OpenStreetMap
            </span>
        </div>
    );
};
